/* ================================================================
   dirtlej Media System V2.2
   Independent players for every Hero / Campaign Panel / Video section.
   Hover mode: no visible control, desktop hover or keyboard focus;
   on touch devices, muted viewport autoplay is used instead.
   ================================================================ */
(() => {
  'use strict';

  if (window.DirtlejMediaV2) {
    window.DirtlejMediaV2.refresh();
    return;
  }

  const SELECTOR = '[data-dirtlej-media]';
  const isMobile = window.matchMedia('(max-width: 749px)');
  const hasHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();

  let visibilityObserver = null;
  let preloadObserver = null;

  class MediaPlayer {
    constructor(root) {
      this.root = root;
      this.mode = root.dataset.playback || 'ambient';
      this.button = root.querySelector('[data-media-toggle]');
      this.videos = {
        desktop: root.querySelector('[data-video-desktop]'),
        mobile: root.querySelector('[data-video-mobile]')
      };

      // Each panel has its own hover target and independent playback state.
      this.hoverTarget = root.closest('.dlj-campaign-panel') || root;
      this.currentVideo = null;
      this.visible = false;
      this.hovered = false;
      this.focused = false;
      this.manual = false;
      this.disposed = false;
      this.playToken = 0;
      this.pendingVideo = null;

      this.onEnter = () => {
        if (this.mode !== 'hover' || !hasHover.matches) return;
        this.hovered = true;
        this.reconcile();
      };

      this.onLeave = () => {
        this.hovered = false;
        if (this.mode === 'hover') this.reconcile();
      };

      this.onFocusIn = () => {
        if (this.mode !== 'hover') return;
        this.focused = true;
        this.reconcile();
      };

      this.onFocusOut = event => {
        if (this.mode !== 'hover') return;
        if (event.relatedTarget && this.root.contains(event.relatedTarget)) return;
        this.focused = false;
        this.reconcile();
      };

      this.onButtonClick = () => this.toggle();

      this.onPlay = event => {
        if (event.target !== this.active()) return;
        this.root.classList.add('is-playing');
        this.root.classList.remove('needs-interaction');
        this.button?.setAttribute('aria-pressed', 'true');
      };

      this.onPause = event => {
        if (event.target !== this.active()) return;
        this.root.classList.remove('is-playing');
        this.button?.setAttribute('aria-pressed', 'false');
      };

      this.onEnded = event => {
        if (event.target !== this.active()) return;
        this.root.classList.remove('is-playing');
        this.button?.setAttribute('aria-pressed', 'false');
        if (this.mode === 'hover') this.manual = false;
      };

      for (const video of Object.values(this.videos)) {
        if (!video) continue;
        video.addEventListener('play', this.onPlay);
        video.addEventListener('pause', this.onPause);
        video.addEventListener('ended', this.onEnded);
      }

      this.button?.addEventListener('click', this.onButtonClick);

      if (this.mode === 'hover') {
        this.hoverTarget.addEventListener('pointerenter', this.onEnter);
        this.hoverTarget.addEventListener('pointerleave', this.onLeave);
        this.root.addEventListener('focusin', this.onFocusIn);
        this.root.addEventListener('focusout', this.onFocusOut);
      }

      preloadObserver?.observe(root);
      visibilityObserver?.observe(root);

      if (!visibilityObserver) {
        // Old browsers without IntersectionObserver still get working video.
        this.visible = true;
        this.reconcile();
      }
    }

    active() {
      const device = isMobile.matches ? 'mobile' : 'desktop';
      if (this.root.dataset[device + 'Kind'] !== 'video') return null;
      return this.videos[device];
    }

    ensureSource(video) {
      if (!video || video.dataset.ready === 'true') return;
      const source = video.dataset.source;
      if (!source) return;
      video.src = source;
      video.dataset.ready = 'true';
      video.load();
    }

    prepare() {
      // Preload only the currently relevant ambient video near the viewport.
      if (this.mode === 'ambient') this.ensureSource(this.active());
    }

    async start(fromClick = false) {
      const video = this.active();
      if (!video || this.disposed || document.hidden) return;
      if (!this.visible && !fromClick) return;

      this.ensureSource(video);
      if (fromClick) this.manual = true;
      if (this.pendingVideo === video || !video.paused) return;

      const token = ++this.playToken;
      this.pendingVideo = video;

      if (this.mode === 'click') {
        this.root.classList.add('is-activated');
        video.controls = true;
        if (fromClick) video.muted = false;
      } else {
        // Ambient and hover previews are always silent.
        video.muted = true;
      }

      try {
        await video.play();
        if (this.disposed || token !== this.playToken) video.pause();
      } catch (_) {
        if (this.disposed || token !== this.playToken) return;
        if (this.mode === 'ambient') this.root.classList.add('needs-interaction');
        if (this.mode === 'click') {
          this.root.classList.remove('is-activated');
          video.controls = false;
          this.manual = false;
        }
      } finally {
        if (this.pendingVideo === video) this.pendingVideo = null;
      }
    }

    stop(reset = false) {
      ++this.playToken;
      this.pendingVideo = null;
      for (const video of Object.values(this.videos)) {
        if (!video) continue;
        video.pause();
        if (reset && video.readyState > 0) {
          try { video.currentTime = 0; } catch (_) { /* No seekable data. */ }
        }
      }
      this.root.classList.remove('is-playing');
      this.button?.setAttribute('aria-pressed', 'false');
    }

    toggle() {
      const video = this.active();
      if (!video) return;
      if (video.paused) {
        this.start(true);
      } else {
        this.manual = false;
        this.stop();
      }
    }

    reconcile() {
      if (this.disposed) return;
      const video = this.active();

      if (video !== this.currentVideo) {
        this.stop();
        this.root.classList.remove('is-activated', 'needs-interaction');
        this.manual = false;
        this.currentVideo = video;
        for (const candidate of Object.values(this.videos)) {
          if (!candidate) continue;
          candidate.classList.toggle('is-active', candidate === video);
          if (candidate !== video) candidate.controls = false;
        }
      }

      if (!video || !this.visible || document.hidden) {
        this.stop();
        return;
      }

      if (this.mode === 'ambient') {
        if (!reduceMotion.matches || this.manual) this.start();
        else this.stop();
        return;
      }

      if (this.mode === 'hover') {
        // Desktop: hover or keyboard focus. Touch: play as it enters view.
        // Reduced motion disables automatic previews but still allows focus.
        const hoverRequested = this.hovered && hasHover.matches;
        const touchRequested = !hasHover.matches;
        const autoRequested = !reduceMotion.matches && (hoverRequested || touchRequested);
        if (this.focused || autoRequested) this.start();
        else this.stop(true);
      }
      // Click mode never starts from a hover or visibility change.
    }

    destroy() {
      this.disposed = true;
      this.stop();
      this.button?.removeEventListener('click', this.onButtonClick);
      this.hoverTarget.removeEventListener('pointerenter', this.onEnter);
      this.hoverTarget.removeEventListener('pointerleave', this.onLeave);
      this.root.removeEventListener('focusin', this.onFocusIn);
      this.root.removeEventListener('focusout', this.onFocusOut);
      for (const video of Object.values(this.videos)) {
        if (!video) continue;
        video.removeEventListener('play', this.onPlay);
        video.removeEventListener('pause', this.onPause);
        video.removeEventListener('ended', this.onEnded);
      }
      visibilityObserver?.unobserve(this.root);
      preloadObserver?.unobserve(this.root);
      instances.delete(this.root);
    }
  }

  if ('IntersectionObserver' in window) {
    visibilityObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const player = instances.get(entry.target);
        if (!player) continue;
        player.visible = entry.isIntersecting && entry.intersectionRatio >= 0.05;
        player.reconcile();
      }
    }, { threshold: [0, 0.05, 0.25, 0.5, 1] });

    preloadObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        instances.get(entry.target)?.prepare();
        preloadObserver.unobserve(entry.target);
      }
    }, { rootMargin: '250px 0px', threshold: 0 });
  }

  function refresh(scope = document) {
    const found = [];
    if (scope.matches?.(SELECTOR)) found.push(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(element => found.push(element));
    for (const element of found) {
      if (!instances.has(element)) {
        // Register before observing: callbacks always resolve the right panel.
        instances.set(element, new MediaPlayer(element));
      }
    }
  }

  function unload(scope) {
    for (const [element, instance] of instances) {
      if (scope === element || scope.contains(element)) instance.destroy();
    }
  }

  function reconcileAll() {
    for (const instance of instances.values()) instance.reconcile();
  }

  isMobile.addEventListener?.('change', reconcileAll);
  hasHover.addEventListener?.('change', reconcileAll);
  reduceMotion.addEventListener?.('change', reconcileAll);
  document.addEventListener('visibilitychange', reconcileAll);
  document.addEventListener('shopify:section:load', event => refresh(event.target));
  document.addEventListener('shopify:section:unload', event => unload(event.target));

  window.DirtlejMediaV2 = { refresh, unload };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh(), { once: true });
  } else {
    refresh();
  }
})();
